import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { empresasApi } from '@/api/apiClient';
import { useAuth } from '@/lib/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function FaturasPage() {
  const { empresa, isLoadingAuth } = useAuth();
  const empresaId = empresa?.id;
  const { data: faturas = [], isPending, error } = useQuery({
    queryKey: ['faturas', empresaId],
    queryFn: () => empresasApi.faturas(),
    enabled: !!empresaId,
  });
  const isLoading = isLoadingAuth || (!!empresaId && isPending);

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-destructive">Erro ao carregar faturas: {error.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusColor = (s) => {
    if (s === 'paid') return 'default';
    if (s === 'overdue' || s === 'past_due') return 'destructive';
    return 'secondary';
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Faturas</h1>
      <Card>
        <CardHeader>
          <CardTitle>Histórico</CardTitle>
        </CardHeader>
        <CardContent>
          {faturas.length === 0 ? (
              <p className="text-muted-foreground">Nenhuma fatura encontrada.</p>
            ) : (
              <ul className="space-y-3">
                {faturas.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-center justify-between py-2 border-b last:border-0"
                  >
                    <div>
                      <span className="font-medium">{f.numeroFatura || f.id.slice(0, 8)}</span>
                      <span className="text-muted-foreground ml-2">
                        Vencimento: {f.dataVencimento ? format(new Date(f.dataVencimento), 'dd/MM/yyyy', { locale: ptBR }) : '-'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>R$ {Number(f.valor).toFixed(2)}</span>
                      <Badge variant={statusColor(f.status)}>{f.status}</Badge>
                      {f.asaasBankSlipUrl && (
                        <a
                          href={f.asaasBankSlipUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline"
                        >
                          Boleto
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
